import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../pages/types';
import ImageEditorTooltip from './Editor/Components/ImageEditorTooltip';
import ImageFullscreen from './image-fullscreen';

interface SingleImageProps extends CommonComponentProps {
  image?: ImageObject;
  imageWidth?: string;
  imageHeight?: string;
  showCaption?: boolean;
}

const SingleImage: React.FC<SingleImageProps> = ({
  id,
  image,
  imageWidth = '100%',
  imageHeight = 'auto',
  showCaption = false,
  sx,
  isEditable,
  onPropsChange,
  onImageUpload,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const firstUrl = (result: string | string[]) =>
    Array.isArray(result) ? result[0] : result;

  const handleAddImage = () => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload((result) => {
        onPropsChange({
          image: { ...(image || {}), url: firstUrl(result) },
        });
      });
    }
  };

  if (!image || !image.url) {
    if (!isEditable) {
      return null;
    }

    return (
      <Box
        id={id}
        sx={{
          maxWidth: '1100px',
          width: '100%',
          margin: '0 auto',
          ...sx,
        }}
      >
        <Box
          onClick={handleAddImage}
          sx={{
            width: imageWidth || '100%',
            height: imageHeight && imageHeight !== 'auto' ? imageHeight : '250px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.05)',
            cursor: 'pointer',
            border: '2px dashed #ccc',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderColor: '#666',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 48, color: '#999' }} />
        </Box>
      </Box>
    );
  }

  // Ensure URL is properly encoded
  const imageUrl = image.url.replace(/ /g, '%20');

  // Create an array with just this single image for the fullscreen viewer
  const images = [image];

  const handleImageClick = () => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload((result) => {
        onPropsChange({
          image: {
            ...image,
            url: firstUrl(result),
          },
        });
      });
    }
  };

  const content = (
    <Box
      component="img"
      src={imageUrl}
      alt={image.altText || 'Image'}
      sx={{
        width: imageWidth ? imageWidth : '100%',
        height: imageHeight ? imageHeight : 'auto',
        cursor: isEditable ? 'pointer' : 'default',
        '&:hover': isEditable
          ? {
              opacity: 0.8,
              outline: '2px dashed #1976d2',
            }
          : {},
      }}
      // onClick={isEditable ? handleImageClick : undefined} // Disabled main click in editor
    />
  );

  return (
    <Box
      id={id}
      sx={{
        maxWidth: '1100px',
        width: '100%',
        textAlign: 'center',
        margin: '0 auto',
        ...sx,
      }}
    >
      {isEditable ? (
        <ImageEditorTooltip
          image={image}
          onEdit={() => {
            if (isEditable && onImageUpload && onPropsChange) {
              onImageUpload((result) => {
                onPropsChange({
                  image: {
                    ...image,
                    url: firstUrl(result),
                  },
                });
              });
            }
          }}
          onDelete={() => {
            if (onPropsChange) {
              onPropsChange({ image: undefined });
            }
          }}
          onTextEdit={(title, altText) => {
            if (onPropsChange) {
              onPropsChange({
                image: {
                  ...image,
                  title,
                  altText,
                },
              });
            }
          }}
        >
          {content}
        </ImageEditorTooltip>
      ) : (
        <ImageFullscreen
          firstImage={image}
          images={images}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        >
          {content}
        </ImageFullscreen>
      )}
    </Box>
  );
};

export default SingleImage;
